import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import FormCardShell from './FormCardShell';
import FormField from './FormField';
import { devError } from '../../lib/devLog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_CONFIG = (form, update) => [
  { id: 'seller-name', label: 'Full Name *', required: true, key: 'fullName', placeholder: 'John Doe' },
  { id: 'seller-email', label: 'Email *', type: 'email', required: true, key: 'email', placeholder: 'john@example.com' },
  { id: 'seller-phone', label: 'Phone', type: 'tel', key: 'phone', placeholder: '(555) 123-4567' },
  { id: 'seller-address', label: 'Property Address', key: 'propertyAddress', placeholder: '123 Main Street' },
  { id: 'seller-city', label: 'City / State', key: 'cityState', placeholder: 'Los Angeles, CA' },
  { id: 'seller-details', label: 'Property Condition / Details', type: 'textarea', key: 'details', placeholder: "Tell us about your property's condition, any repairs needed, timeline, etc." },
].map((f) => ({ ...f, value: form[f.key], onChange: update(f.key) }));

const useSellerSubmit = ({ source, sourceUrl }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (form) => {
    setLoading(true);
    try {
      await axios.post(`${API}/seller-leads`, { ...form, source, sourceUrl });
      setSubmitted(true);
      toast.success("Cash offer request submitted! We'll contact you within 24 hours.");
    } catch (err) {
      devError('seller form submit failed:', err.message);
      toast.error('Failed to submit your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return { submitted, loading, submit };
};

const SellerForm = ({ defaultCityState = '', source = 'home' }) => {
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', propertyAddress: '',
    cityState: defaultCityState, details: '',
  });
  const sourceUrl = typeof window !== 'undefined' ? window.location.href : '';
  const { submitted, loading, submit } = useSellerSubmit({ source, sourceUrl });
  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const fields = FIELD_CONFIG(form, update);

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(form);
  };

  return (
    <FormCardShell
      accent="emerald"
      icon={Home}
      title="Get a Cash Offer"
      description="Submit your property details and receive a fair cash offer within 24 hours."
      submitted={submitted}
      thankYouTitle="Thank You!"
      thankYouBody="We've received your submission. A member of our team will reply within the next 24 hours."
      contactCta="Contact us at:"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {fields.map((f) => <FormField key={f.id} {...f} />)}
        <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 transition-all duration-200 hover:shadow-lg">
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
          ) : (
            <><Send className="w-4 h-4 mr-2" />Request My Cash Offer</>
          )}
        </Button>
      </form>
    </FormCardShell>
  );
};

export default SellerForm;
